import { useState } from "react";
import { httpsCallable } from "firebase/functions";
import ProductShell from "../components/ProductShell";
import { useAuth } from "../context/AuthContext";
import { functions } from "../firebase";
import { formatPublicHandle } from "../utils/identity";

const planOptions = [
  { id: "free", label: "Basic" },
  { id: "pro", label: "Pro" },
  { id: "team", label: "Team" }
];

export default function Admin() {
  const { user, userProfile, workspace } = useAuth();
  const [lookupIdentifier, setLookupIdentifier] = useState("");
  const [overrideIdentifier, setOverrideIdentifier] = useState("");
  const [overridePlanId, setOverridePlanId] = useState("pro");
  const [repairWorkspaceId, setRepairWorkspaceId] = useState("");
  const [repairOwnerId, setRepairOwnerId] = useState("");
  const [repairWorkspaceName, setRepairWorkspaceName] = useState("");
  const [busyAction, setBusyAction] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const runAdminAction = async (actionName, runner) => {
    setBusyAction(actionName);
    setStatus("");
    setError("");

    try {
      await runner();
    } catch (actionError) {
      setError(actionError.message || "Admin action failed.");
    } finally {
      setBusyAction("");
    }
  };

  const handleLookup = async () => {
    await runAdminAction("lookup", async () => {
      const lookupFn = httpsCallable(functions, "adminLookupUser");
      const response = await lookupFn({ identifier: lookupIdentifier.trim() });
      setLookupResult(response.data);
      setStatus("User lookup completed.");
      setOverrideIdentifier(response.data?.user?.uid || lookupIdentifier.trim());
      setRepairWorkspaceId(response.data?.workspace?.id || "");
      setRepairOwnerId(response.data?.workspace?.ownerId || response.data?.user?.uid || "");
      setRepairWorkspaceName(response.data?.workspace?.name || "");
    });
  };

  const handleOverridePlan = async () => {
    await runAdminAction("override", async () => {
      const overrideFn = httpsCallable(functions, "adminOverridePlan");
      const response = await overrideFn({
        identifier: overrideIdentifier.trim(),
        planId: overridePlanId
      });
      setStatus(`Plan override applied. ${response.data.updatedUsers} active user account(s) were synced.`);
    });
  };

  const handleOverrideCurrentAdmin = async () => {
    await runAdminAction("override-self", async () => {
      const overrideFn = httpsCallable(functions, "adminOverridePlan");
      const response = await overrideFn({
        identifier: user.uid,
        planId: overridePlanId
      });
      setOverrideIdentifier(user.uid);
      setStatus(`Your admin account is now on ${planOptions.find((plan) => plan.id === overridePlanId)?.label}. ${response.data.updatedUsers} active user account(s) were synced.`);
    });
  };

  const handleRepairWorkspace = async () => {
    await runAdminAction("repair", async () => {
      const repairFn = httpsCallable(functions, "adminRepairWorkspace");
      const response = await repairFn({
        workspaceId: repairWorkspaceId.trim(),
        ownerId: repairOwnerId.trim(),
        workspaceName: repairWorkspaceName.trim()
      });
      setStatus(`Workspace repaired. ${response.data.updatedUsers} active user account(s) were synced.`);
    });
  };

  if (!userProfile?.isAdmin) {
    return (
      <ProductShell
        subtitle="This area is reserved for the AffiTrack platform admin account."
        title="Admin Access"
      >
        <div className="dashboard-shell product-page">
          <section className="card admin-card">
            <h2>Admin role not enabled yet</h2>
            <p className="muted-text">
              To make your account the platform admin, open your Firestore user document and set
              <strong> appRole</strong> to <strong>admin</strong>.
            </p>
            <p className="muted-text">
              User document path: <code>users/{user?.uid}</code>
            </p>
          </section>
        </div>
      </ProductShell>
    );
  }

  return (
    <ProductShell
      subtitle="Use secure backend-only actions to inspect accounts, override plans, and repair workspace records."
      title="Platform Admin"
    >
      <div className="dashboard-shell product-page">
        {status ? <p className="status success-text mobile-banner">{status}</p> : null}
        {error ? <p className="status error-text mobile-banner">{error}</p> : null}

        <section className="dashboard-grid metrics-grid admin-grid">
          <article className="card admin-card mobile-section-card">
            <span className="eyebrow">Admin Account</span>
            <h2>{userProfile?.publicHandle || formatPublicHandle({ email: user?.email })}</h2>
            <p className="muted-text">
              {user?.email} | App role: <strong>{userProfile?.appRole}</strong>
            </p>
          </article>

          <article className="card admin-card mobile-section-card">
            <span className="eyebrow">Current Workspace</span>
            <h2>{workspace?.name || userProfile?.workspaceName || "My Workspace"}</h2>
            <p className="muted-text">
              Plan: <strong>{workspace?.planName || userProfile?.planName}</strong>
            </p>
            <button
              className="mobile-action-button"
              disabled={busyAction === "override-self"}
              onClick={handleOverrideCurrentAdmin}
              type="button"
            >
              {busyAction === "override-self" ? "Upgrading..." : `Upgrade My Account to ${planOptions.find((plan) => plan.id === overridePlanId)?.label}`}
            </button>
          </article>
        </section>

        <section className="dashboard-grid metrics-grid admin-grid">
          <article className="card admin-card mobile-section-card">
            <h2>User Lookup</h2>
            <label className="field-label" htmlFor="lookup-identifier">
              User email, username, or uid
            </label>
            <input
              id="lookup-identifier"
              placeholder="owner@affitrack.net"
              value={lookupIdentifier}
              onChange={(event) => setLookupIdentifier(event.target.value)}
            />
            <button
              className="mobile-action-button"
              disabled={busyAction === "lookup" || !lookupIdentifier.trim()}
              onClick={handleLookup}
              type="button"
            >
              {busyAction === "lookup" ? "Looking up..." : "Lookup User"}
            </button>
          </article>

          <article className="card admin-card mobile-section-card">
            <h2>Plan Override</h2>
            <label className="field-label" htmlFor="override-identifier">
              Workspace owner email, username, or uid
            </label>
            <input
              id="override-identifier"
              placeholder="owner@affitrack.net"
              value={overrideIdentifier}
              onChange={(event) => setOverrideIdentifier(event.target.value)}
            />

            <label className="field-label" htmlFor="override-plan">
              New plan
            </label>
            <select
              id="override-plan"
              value={overridePlanId}
              onChange={(event) => setOverridePlanId(event.target.value)}
            >
              {planOptions.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.label}
                </option>
              ))}
            </select>

            <button
              className="mobile-action-button"
              disabled={busyAction === "override" || !overrideIdentifier.trim()}
              onClick={handleOverridePlan}
              type="button"
            >
              {busyAction === "override" ? "Applying..." : "Apply Override"}
            </button>
          </article>
        </section>

        <section className="card admin-card mobile-section-card">
          <h2>Workspace Repair</h2>
          <div className="dashboard-grid admin-grid">
            <div>
              <label className="field-label" htmlFor="repair-workspace-id">
                Workspace id
              </label>
              <input
                id="repair-workspace-id"
                placeholder="workspace uid"
                value={repairWorkspaceId}
                onChange={(event) => setRepairWorkspaceId(event.target.value)}
              />
            </div>

            <div>
              <label className="field-label" htmlFor="repair-owner-id">
                Owner uid
              </label>
              <input
                id="repair-owner-id"
                placeholder="owner uid"
                value={repairOwnerId}
                onChange={(event) => setRepairOwnerId(event.target.value)}
              />
            </div>
          </div>

          <label className="field-label" htmlFor="repair-workspace-name">
            Workspace name override
          </label>
          <input
            id="repair-workspace-name"
            placeholder="AffiTrack Mentor Group"
            value={repairWorkspaceName}
            onChange={(event) => setRepairWorkspaceName(event.target.value)}
          />

          <button
            className="mobile-action-button"
            disabled={busyAction === "repair" || !repairWorkspaceId.trim()}
            onClick={handleRepairWorkspace}
            type="button"
          >
            {busyAction === "repair" ? "Repairing..." : "Repair Workspace"}
          </button>

          <p className="muted-text">
            Repair syncs the workspace document, restores the owner membership, and updates active members to the
            workspace plan limits.
          </p>
        </section>

        {lookupResult ? (
          <section className="card admin-card mobile-section-card">
            <h2>Lookup Result</h2>
            <div className="dashboard-grid admin-grid">
              <article className="admin-result">
                <span className="eyebrow">User</span>
                <p><strong>Username:</strong> {lookupResult.user?.username ? `@${lookupResult.user.username}` : "Unknown"}</p>
                <p><strong>Email:</strong> {lookupResult.user?.email || "Unknown"}</p>
                <p><strong>UID:</strong> {lookupResult.user?.uid}</p>
                <p><strong>Plan:</strong> {lookupResult.user?.planName}</p>
                <p><strong>Workspace Role:</strong> {lookupResult.user?.workspaceRole}</p>
                <p><strong>Subscription:</strong> {lookupResult.user?.subscriptionStatus || "None"}</p>
              </article>

              <article className="admin-result">
                <span className="eyebrow">Workspace</span>
                <p><strong>Name:</strong> {lookupResult.workspace?.name || "Missing"}</p>
                <p><strong>ID:</strong> {lookupResult.workspace?.id || "Missing"}</p>
                <p><strong>Owner:</strong> {lookupResult.workspace?.ownerId || "Missing"}</p>
                <p><strong>Plan:</strong> {lookupResult.workspace?.planName || "Missing"}</p>
                <p><strong>Active Members:</strong> {lookupResult.workspace?.activeMemberCount ?? 0}</p>
              </article>
            </div>

            <div className="link-list">
                {lookupResult.members?.map((member) => (
                <article className="link-card mobile-link-card" key={member.uid}>
                  <div>
                    <h3>{formatPublicHandle(member, member.email)}</h3>
                    <p className="muted-text">{member.email || member.uid}</p>
                  </div>
                  <div className="link-meta">
                    <span>Role: {member.role}</span>
                    <span>Status: {member.status}</span>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    </ProductShell>
  );
}
